import { Component, DestroyRef, inject, OnDestroy, Type } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgOptimizedImage } from '@angular/common';
import { Router } from '@angular/router';
import { first } from 'rxjs/operators';

import { DialogService, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';

import { AuthService } from '../../services/auth/auth.service';
import { LoginComponent } from 'src/app/module/auth/pages/login/login.component';
import { TermsDialogComponent } from 'src/app/module/config/pages/terms-dialog/terms-dialog.component';
import { PrivacyPolicyComponent } from 'src/app/module/config/pages/privacy-policy/privacy-policy.component';
import { AboutDialogComponent } from 'src/app/module/config/pages/about-dialog/about-dialog.component';
import { HelpChatService } from 'src/app/shared/services/help-chat/help-chat.service';

import { ButtonModule } from 'primeng/button';

type InstitutionalDocument = 'terms' | 'privacy' | 'about';

interface InstitutionalDialog {
  title: string;
  component: Type<unknown>;
}

const INSTITUTIONAL_DIALOGS: Record<InstitutionalDocument, InstitutionalDialog> = {
  terms: {
    title: 'Termos de Uso',
    component: TermsDialogComponent,
  },
  privacy: {
    title: 'Política de Privacidade',
    component: PrivacyPolicyComponent,
  },
  about: {
    title: 'Quem Somos',
    component: AboutDialogComponent,
  },
};

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  imports: [NgOptimizedImage, DynamicDialogModule, ButtonModule],
})
export class FooterComponent implements OnDestroy {
  public auth = inject(AuthService);
  public dialogService = inject(DialogService);
  private router = inject(Router);
  private readonly helpChatService = inject(HelpChatService);
  private readonly destroyRef = inject(DestroyRef);

  ref: DynamicDialogRef | undefined | null;
  readonly currentYear = new Date().getFullYear();

  openInstitutionalDialog(document: InstitutionalDocument, event: MouseEvent): void {
    if (
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.stopImmediatePropagation();
    event.preventDefault();

    if (this.ref) {
      this.ref.close();
      this.ref = undefined;
    }

    const dialog = INSTITUTIONAL_DIALOGS[document];
    this.ref = this.dialogService.open(dialog.component, {
      header: dialog.title,
      width: 'min(92vw, 950px)',
      modal: true,
      closable: true,
      closeOnEscape: true,
      dismissableMask: true,
      style: {
        maxHeight: '90vh',
      },
      contentStyle: {
        overflow: 'auto',
        maxHeight: 'calc(90vh - 5rem)',
      },
    });

    this.ref?.onClose.pipe(first(), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.ref = undefined;
    });
  }

  openModalLogin(type: number, event?: Event) {
    if (event) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }

    if (this.ref) {
      this.ref.close();
      this.ref = undefined;
    }

    if (this.auth.isLogged() && type === 0) {
      this.router.navigate(['/perfil']);
    } else if (this.auth.isLogged() && this.auth.isCustomer() && type === 1) {
      this.router.navigate(['/perfil'], { queryParams: { tab: 2 } });
    } else if (this.auth.isLogged() && this.auth.isProducer() && type === 1) {
      this.router.navigate(['/eventos/meus-eventos']);
    } else {
      this.ref = this.dialogService.open(LoginComponent, {
        header: '',
        height: 'auto',
        modal: true,
        closable: true,
        dismissableMask: true,
        showHeader: true,
        style: {
          width: '95vw',
          maxWidth: '540px',
          borderRadius: '20px',
        },
        data: {
          tab: type,
        },
      });

      this.ref?.onClose.pipe(first(), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.ref = undefined;
      });
    }
  }

  openHelpChat(event?: Event): void {
    if (event) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }

    if (this.ref) {
      this.ref.close();
      this.ref = undefined;
    }

    this.helpChatService.open();
  }

  ngOnDestroy() {
    if (this.ref) {
      this.ref.close();
    }
  }
}
